/**
 * @flow
 */

import React from 'react';
import gql from 'graphql-tag';
import { connect } from 'react-redux';
import { map, isEmpty, find } from 'lodash';
import { format, addMinutes, startOfDay } from 'date-fns';
import { useWatch } from 'react-hook-form';
import { getFreeTimeSpans, getInvertedTimeSpans } from '../../../../lib/schedule';
import { TimeSpanSelect } from '../../../../components';
import client from '../../../../apollo-client';

// Query to get salon settings
const GET_SALON_QUERY = gql`
    query getSalonSettings($salon: ID!) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    settings {
                        schedule {
                            startAt
                            endAt
                            step
                            format
                        }
                    }
                }
            }
        }
    }
`;

function TimeSpanSelectContainer({ value, salon, masters, loading, onChange, dispatch, ...rest }) {
    // Read the relevant form values via useWatch (works inside a FormProvider)
    const masterId = useWatch({ name: 'master' });
    const serviceId = useWatch({ name: 'service' });
    const duration = useWatch({ name: 'duration' });

    // Get salon settings from Apollo local cache
    let schedule = { startAt: 480, endAt: 1200, step: 30, format: 'HH:mm' };
    try {
        const data = client.readQuery({
            query: GET_SALON_QUERY,
            variables: { salon },
        });
        if (data && data.viewer && data.viewer.salon) {
            schedule = data.viewer.salon.settings.schedule;
        }
    } catch (e) {
        // cache not populated yet
    }

    const master = find(masters, m => m.id === masterId);

    const disabled = loading || !serviceId || !master || !duration;

    let options = [];
    if (!disabled) {
        // Generate list of time spans when master is not working at this date
        const notWorkingTime = getInvertedTimeSpans({
            startAt: schedule.startAt,
            endAt: schedule.endAt,
            timeSpans: map(master.schedule, workingTime => [
                schedule.step === 60 && (workingTime.startAt / 30) % 2 > 0
                    ? workingTime.startAt - 30
                    : workingTime.startAt,
                schedule.step === 60 && (workingTime.endAt / 30) % 2 > 0
                    ? workingTime.endAt + 30
                    : workingTime.endAt,
            ]),
        });

        const freeTimeSpans = getFreeTimeSpans({
            timeSpan: [schedule.startAt, schedule.endAt],
            duration,
            exclude: notWorkingTime,
            step: schedule.step,
        });

        options = map(freeTimeSpans, timeSpan => ({
            text: [
                format(addMinutes(startOfDay(new Date()), timeSpan[0]), schedule.format),
                format(addMinutes(startOfDay(new Date()), timeSpan[1]), schedule.format),
            ].join(' - '),
            value: timeSpan[0],
        }));
    }

    // If there is a value but no options, show the value as a text placeholder
    let text = null;
    if (value && isEmpty(options)) {
        text = format(addMinutes(startOfDay(new Date()), value), schedule.format);
    }

    return (
        <TimeSpanSelect
            value={value}
            options={options}
            disabled={disabled}
            text={text}
            onChange={onChange}
            {...rest}
        />
    );
}

export default connect(({ user }) => ({
    salon: user.get('salon'),
}))(TimeSpanSelectContainer);
